import { defineStore } from 'pinia';
import { ref } from 'vue';
import { searchGlobal, type SearchResponse } from '@/api/search';

/**
 * Global search store (E17).
 *
 * Owns: query text, current results, loading state, keyboard selection index,
 * and the 10-entry "recent queries" list persisted in localStorage.
 *
 * Responsibilities the store handles (so components stay dumb):
 *   - Debounce typing at 200ms
 *   - Cancel in-flight requests via AbortController when the user types
 *     faster than the server responds
 *   - Enforce a 2-char minimum before hitting the API
 *   - Persist recent queries on successful navigation (not on every keystroke)
 */

const RECENT_KEY = 'cwr:search:recent';
const RECENT_LIMIT = 10;
const DEBOUNCE_MS = 200;
const MIN_QUERY_LEN = 2;

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export const useSearchStore = defineStore('search', () => {
  const query = ref('');
  const results = ref<SearchResponse | null>(null);
  const loading = ref(false);
  const selectedIndex = ref(0);
  const recent = ref<string[]>(loadRecent());

  let abortController: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  async function runSearch(q: string) {
    query.value = q;
    selectedIndex.value = 0;

    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    if (q.trim().length < MIN_QUERY_LEN) {
      results.value = null;
      loading.value = false;
      return;
    }

    debounceTimer = setTimeout(async () => {
      abortController = new AbortController();
      loading.value = true;
      try {
        results.value = await searchGlobal(q, abortController.signal);
      } catch (err: unknown) {
        // Axios wraps AbortController cancellations as CanceledError — swallow them.
        const name = (err as { name?: string } | null)?.name;
        if (name !== 'CanceledError' && name !== 'AbortError') {
          results.value = null;
        }
      } finally {
        loading.value = false;
      }
    }, DEBOUNCE_MS);
  }

  function rememberQuery(q: string) {
    const trimmed = q.trim();
    if (!trimmed) return;
    recent.value = [trimmed, ...recent.value.filter((r) => r !== trimmed)].slice(0, RECENT_LIMIT);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(recent.value));
    } catch {
      // localStorage quota / private mode — non-fatal, just skip persistence.
    }
  }

  function clear() {
    query.value = '';
    results.value = null;
    selectedIndex.value = 0;
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  }

  return {
    query,
    results,
    loading,
    selectedIndex,
    recent,
    runSearch,
    rememberQuery,
    clear,
  };
});
